import { Dot, Popover } from "@xola/ui-kit";
import { RowNode } from "ag-grid-community";
import clsx from "clsx";
import { getWaiverParticipantCount } from "common/src/models/Waiver";
import { sumBy } from "lodash";
import React from "react";
import { Guest, PurchaseItem, Waiver } from "types";
import { LinkedWaiverParticipantPopover } from "../../../PurchaseModification/Waiver/LinkedWaiverParticipantPopover";
import { LinkedWaiverPopover } from "../../../PurchaseModification/Waiver/LinkedWaiverPopover";
import { WaiverParticipantDetails } from "../WaiverParticipantDetails";
import { RowData } from "./types";

interface WaiverItemProps {
    waivers: Waiver[];
    total?: number;
    item?: PurchaseItem;
}

export const waiverCellValueGetter = ({ data }: { data: RowData }) => {
    if ("waivers" in data) {
        return data.waiverQuantity;
    }

    return { totalWaivers: sumBy(data.item.waivers, (waiver) => waiver.participants?.length) ?? 0 };
};

export const WaiverItem = ({ waivers, total, item }: WaiverItemProps) => {
    return item ? (
        <LinkedWaiverPopover guests={item.guests} purchaseItem={item} />
    ) : (
        <WaiverItemPopover total={total} waivers={waivers} />
    );
};

const WaiverItemPopover = ({ total, waivers }: { total?: number; waivers: Waiver[] }) => {
    const waiverParticipantCount = getWaiverParticipantCount(waivers);

    return (
        <Popover placement="top" appendTo={getAppendRosterTarget()}>
            <WaiverDetails count={waiverParticipantCount} total={total} />
            <Popover.Content className="max-h-75 overflow-y-auto whitespace-pre-wrap break-all p-3 leading-p1 tracking-tightest">
                {waivers.map((waiver) => (
                    <div key={waiver?.id} className="mb-2 flex">
                        <WaiverParticipantDetails waiver={waiver} />
                    </div>
                ))}
            </Popover.Content>
        </Popover>
    );
};

export interface WaiverDetailsProps {
    count: number;
    item?: PurchaseItem;
    guest?: Guest;
    waivers?: Waiver[];
    total?: number;
}

export const WaiverDetails = ({ count, waivers, total, guest, item }: WaiverDetailsProps) => {
    const isLinked = !!guest?.participant;

    if (isLinked) {
        return null; // show nothing if linked
    }

    const waiverParticipantIds =
        waivers?.flatMap((waiver) => (waiver ? waiver.participants?.map((participant) => participant.id) : [])) ?? [];
    const linkedIds = new Set(item?.guests.map((guest) => guest.participant));
    // Check if there are any participant IDs which is not linked to any Guest
    const hasAnyUnlinkedParticipant = waiverParticipantIds?.some((participantId) => !linkedIds.has(participantId));

    return (
        <span className="flex items-center">
            {total ? (
                <span>
                    {count}/{total}
                </span>
            ) : !hasAnyUnlinkedParticipant || waivers?.length === 0 ? (
                "Unsigned" // show unsigned if no participant left to link or no has waiver signed
            ) : hasAnyUnlinkedParticipant && guest && item ? (
                <span className="px-1">
                    <LinkedWaiverParticipantPopover
                        hideLinkWaiverButton
                        showLinkText
                        guest={guest}
                        purchaseItem={item}
                        appendTo={getAppendRosterTarget()}
                    />
                </span>
            ) : null}
        </span>
    );
};

export const WaiverSummaryRowCell = ({
    count,
    total,
    isExpanded,
}: {
    count: number;
    total: number;
    isExpanded: boolean;
}) => {
    const unsignedCount = count > total ? 0 : total - count;
    return (
        <Popover appendTo={document.body} disabled={isExpanded}>
            <div className={isExpanded ? "flex flex-col" : "flex items-center gap-3"}>
                <div className="flex items-center gap-1">
                    <Dot color="success" />
                    <span>
                        {count} {isExpanded ? "Signed" : null}
                    </span>
                </div>
                <div className={clsx(isExpanded && "-mt-4", "flex items-center gap-1")}>
                    <Dot color="danger" />
                    <span>
                        {unsignedCount} {isExpanded ? "Unsigned" : null}
                    </span>
                </div>
            </div>
            <Popover.Content>
                <div className="min-w-[160px] p-3">
                    <div className="flex items-center justify-between space-x-1">
                        <div className="flex items-center space-x-1">
                            <Dot color="success" />
                            <span>Signed</span>
                        </div>
                        <span>{count}</span>
                    </div>
                    <div className="flex items-center justify-between space-x-1">
                        <div className="flex items-center space-x-1">
                            <Dot color="danger" />
                            <span>Unsigned</span>
                        </div>
                        <span>{unsignedCount}</span>
                    </div>
                    <hr className="mt-2 mb-2 text-gray-light" />
                    <div className="flex items-center justify-between space-x-1">
                        <span>Total</span>
                        <span>{count + unsignedCount}</span>
                    </div>
                </div>
            </Popover.Content>
        </Popover>
    );
};

export const WaiversCellRenderer = ({ data, node }: { data: RowData; node: RowNode }) => {
    if ("isWaiverLinked" in data && !data.isWaiverLinked) {
        return null;
    }
    if ("waivers" in data) {
        const waiverParticipantCount = sumBy(data.waivers, (waiver) => waiver.participants?.length) ?? 0;

        if (waiverParticipantCount === 0 && data.waiverQuantity === 0) {
            return null;
        }

        return (
            <WaiverSummaryRowCell
                isExpanded={node.expanded}
                count={waiverParticipantCount}
                total={data.waiverQuantity}
            />
        );
    }

    const waivers = data?.item?.waivers;

    return data.isOrganizer && waivers ? (
        <WaiverItem waivers={waivers} total={data.item.quantity} item={data.item} />
    ) : (
        <WaiverDetails waivers={waivers} count={0} item={data.item} guest={data.guest} />
    );
};

export const getAppendRosterTarget = (): HTMLElement | undefined => {
    if (typeof document === "undefined") return undefined;

    return (
        (document.querySelector("#purchase-drawer") as HTMLElement) ??
        (document.querySelector("#roster-drawer") as HTMLElement) ??
        document.body
    );
};
